import React from 'react';
import { Input, Button, Spin, Select } from 'antd';
import { Controller } from 'react-hook-form';
import { get } from 'lodash';
import { StyledForm, FormRow, Field } from './styled';
import { useSettingForm } from './hooks';
import { SettingTypes } from '.';
import LabelHoc from './LabelHoc';
import { validateWholeNumber, validateListLength, validateNumber } from './ValidationUtils';

const AttemptsMessage = 'Value should be > 0';
const RangeMessage = 'Value should be between 0 to 100';

function BrokenAssets({ data = {} }) {
  const { control, handleSubmit, errors, mutation, onSubmit, readOnly } = useSettingForm({
    data,
    type: SettingTypes.brokenAssests,
  });

  const hasError = Object.values(errors).length > 0;

  const options = get(data, 'threshold.IgnoreDimensions.OBJECT_NAME.values', []);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Spin tip="Saving..." spinning={mutation.isLoading}>
        <FormRow>
          <LabelHoc text="Impacted Unique Devices" err={errors.UniqueDevices} />
          <Field>
            <Controller
              render={({ field }) => (
                <Input addonBefore=">=" defaultValue="20" {...field} readOnly={readOnly} />
              )}
              name="UniqueDevices"
              control={control}
              defaultValue="20"
              rules={{
                required: 'Impacted Unique Devices is required',
                min: { value: 0, message: RangeMessage },
                validate: validateWholeNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <LabelHoc text="Attempts" err={errors.Baseline} />
          <Field>
            <Controller
              render={({ field }) => <Input addonBefore=">=" {...field} readOnly={readOnly} />}
              name="Baseline"
              control={control}
              rules={{
                required: 'Attempts is required',
                min: { value: 0, message: AttemptsMessage },
                validate: validateWholeNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <LabelHoc text="VSF-T" err={errors.VideoStartFailuresTechPercentage} />
          <Field>
            <Controller
              render={({ field }) => (
                <Input
                  addonBefore=">="
                  defaultValue="10"
                  addonAfter="%"
                  {...field}
                  readOnly={readOnly}
                />
              )}
              name="VideoStartFailuresTechPercentage"
              control={control}
              rules={{
                required: 'VSF-T is required',
                min: { value: 0, message: RangeMessage },
                max: { value: 100, message: RangeMessage },
                validate: validateNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <LabelHoc text="VPF-T" err={errors.VideoPlaybackFailuresTechPercentage} />
          <Field>
            <Controller
              render={({ field }) => (
                <Input
                  addonBefore=">="
                  defaultValue="10"
                  addonAfter="%"
                  {...field}
                  readOnly={readOnly}
                />
              )}
              name="VideoPlaybackFailuresTechPercentage"
              control={control}
              rules={{
                required: 'VPF-T is required',
                min: { value: 0, message: RangeMessage },
                max: { value: 100, message: RangeMessage },
                validate: validateNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <LabelHoc text="Abandonment" err={errors.HighExitBeforeVideoStartPercentage} />
          <Field>
            <Controller
              render={({ field }) => (
                <Input
                  addonBefore=">="
                  defaultValue="10"
                  addonAfter="%"
                  {...field}
                  readOnly={readOnly}
                />
              )}
              name="HighExitBeforeVideoStartPercentage"
              control={control}
              rules={{
                required: 'Abandonment is required',
                min: { value: 0, message: RangeMessage },
                max: { value: 100, message: RangeMessage },
                validate: validateNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <LabelHoc text="CIRR" err={errors.ConnectionInducedRebufferingRatio} />
          <Field>
            <Controller
              render={({ field }) => (
                <Input
                  addonBefore=">="
                  defaultValue="5"
                  addonAfter="%"
                  {...field}
                  readOnly={readOnly}
                />
              )}
              name="ConnectionInducedRebufferingRatio"
              control={control}
              rules={{
                required: 'CIRR is required',
                min: { value: 0, message: RangeMessage },
                max: { value: 100, message: RangeMessage },
                validate: validateNumber,
              }}
            />
          </Field>
        </FormRow>

        <FormRow>
          <div style={{ width: '100%' }}>
            <LabelHoc text="Ignore Assets containing" err={errors.OBJECT_NAME} />
            <Field style={{ width: '100%' }}>
              <Controller
                render={({ field }) => (
                  <Select
                    mode="tags"
                    placeholder="Enter exclude strings"
                    defaultValue={options}
                    style={{ width: '100%', maxHeight: '100px', height: 'auto', overflowY: 'auto' }}
                    {...field}
                    disabled={readOnly}
                    open={false}
                    maxTagTextLength={10}
                  />
                )}
                name="OBJECT_NAME"
                control={control}
                rules={{ validate: validateListLength }}
              />
            </Field>
          </div>
        </FormRow>

        <FormRow>
          <span />
          <Button htmlType="submit" disabled={readOnly || mutation.isLoading || hasError}>
            Save
          </Button>
        </FormRow>
      </Spin>
    </StyledForm>
  );
}

export default BrokenAssets;
